import { useEffect, useRef, useState } from "react";
import { VoxelRenderer } from "../render/VoxelRenderer.ts";
import { useEditorStore } from "../store/editorStore";
import type {
  CameraView,
  TextureStatus,
  VoxelHit
} from "../types/editor";

interface WalkViewportStatus {
  enabled: boolean;
  pointerLocked: boolean;
  grounded: boolean;
  position: {
    x: number;
    y: number;
    z: number;
  };
}

const INITIAL_WALK_STATUS: WalkViewportStatus = {
  enabled: false,
  pointerLocked: false,
  grounded: false,
  position: { x: 0, y: 0, z: 0 }
};

const VISIBILITY_STORAGE_KEY = "voxel-studio-visibility-boost";
const DEFAULT_VISIBILITY_BOOST = 32;

function readStoredVisibilityBoost(): number {
  try {
    const storedValue = window.localStorage.getItem(
      VISIBILITY_STORAGE_KEY
    );
    if (storedValue === null) {
      return DEFAULT_VISIBILITY_BOOST;
    }
    const parsedValue = Number(storedValue);
    if (!Number.isFinite(parsedValue)) {
      return DEFAULT_VISIBILITY_BOOST;
    }
    return Math.min(100, Math.max(0, Math.round(parsedValue)));
  } catch {
    return DEFAULT_VISIBILITY_BOOST;
  }
}

export function ViewportPanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeCamera, setActiveCamera] =
    useState<CameraView>("perspective");
  const [walkStatus, setWalkStatus] =
    useState<WalkViewportStatus>(INITIAL_WALK_STATUS);
  const [visibilityBoost, setVisibilityBoost] =
    useState<number>(readStoredVisibilityBoost);
  const activeTool = useEditorStore(
    (state) => state.activeTool
  );
  const rendererInstance = useEditorStore(
    (state) => state.renderer
  );
  const assetLoader = useEditorStore((state) => state.assetLoader);
  const projectVersionId = useEditorStore((state) => state.projectVersionId);
  const visibleMaximumY = useEditorStore(
    (state) => state.visibleMaximumY
  );
  const setVisibleMaximumY = useEditorStore(
    (state) => state.setVisibleMaximumY
  );
  const cursorPosition = useEditorStore(
    (state) => state.cursorPosition
  );
  const toastMessage = useEditorStore(
    (state) => state.toastMessage
  );
  const toastNonce = useEditorStore((state) => state.toastNonce);
  const clearToast = useEditorStore((state) => state.clearToast);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const state = useEditorStore.getState();
    if (!state.projectVersionId) return;

    const renderer = new VoxelRenderer(
      container,
      state.world,
      state.assetLoader
    );
    state.bindRenderer(renderer);
    renderer.setVisibleMaximumY(state.visibleMaximumY);
    renderer.setVisibilityBoost(visibilityBoost);
    renderer.setTextureStatusListener((status: TextureStatus) => {
      useEditorStore.getState().setTextureStatus(status);
    });
    renderer.setPointerMoveListener((hit: VoxelHit | null) => {
      useEditorStore.getState().setCursorPosition(
        hit ? (hit.isGround ? hit.adjacent : hit.position) : null
      );
    });
    renderer.setPointerClickListener((hit: VoxelHit | null) => {
      useEditorStore.getState().handleViewportHit(hit);
    });
    renderer.setWalkStatusListener(
      (status: WalkViewportStatus) => {
        setWalkStatus(status);
      }
    );
    renderer.setPointerDragListener((
      startHit: VoxelHit | null,
      endHit: VoxelHit | null,
      phase: "start" | "move" | "end"
    ) => {
      useEditorStore.getState().handleSelectionDrag(
        startHit,
        endHit,
        phase === "end"
      );
    });
    renderer.frameWorld();

    return () => {
      if (useEditorStore.getState().renderer === renderer) {
        useEditorStore.getState().bindRenderer(null);
      }
      renderer.destroy();
    };
  }, [assetLoader, projectVersionId]);

  useEffect(() => {
    rendererInstance?.setSelectionDragEnabled(
      activeTool === "select"
    );
  }, [rendererInstance, activeTool]);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = window.setTimeout(clearToast, 1800);
    return () => window.clearTimeout(timer);
  }, [toastMessage, toastNonce, clearToast]);

  useEffect(() => {
    rendererInstance?.setVisibilityBoost(visibilityBoost);
  }, [rendererInstance, visibilityBoost]);

  const updateVisibilityBoost = (value: number) => {
    const nextValue = Math.min(
      100,
      Math.max(0, Math.round(value))
    );
    setVisibilityBoost(nextValue);
    try {
      window.localStorage.setItem(
        VISIBILITY_STORAGE_KEY,
        String(nextValue)
      );
    } catch {
      // Storage can be unavailable in private/restricted browser contexts.
    }
  };

  const setCamera = (camera: CameraView) => {
    rendererInstance?.exitWalkMode();
    setActiveCamera(camera);
    useEditorStore.getState().setCameraView(camera);
  };

  const toggleWalkMode = () => {
    if (!rendererInstance) return;

    if (walkStatus.enabled) {
      rendererInstance.exitWalkMode();
      return;
    }

    setActiveCamera("perspective");
    rendererInstance.enterWalkMode();
  };

  const formatWalkCoordinate = (value: number) =>
    Number.isFinite(value) ? value.toFixed(1) : "—";

  return (
    <section className="viewport-shell">
      <div ref={containerRef} className="three-viewport" />

      <div className="viewport-top-overlay">
        <div className="viewport-pills">
          {(["perspective", "top", "front"] as const).map(
            (camera) => (
              <button
                key={camera}
                type="button"
                className={
                  "viewport-pill" +
                  (activeCamera === camera ? " active" : "")
                }
                onClick={() => setCamera(camera)}
              >
                {camera[0].toUpperCase() + camera.slice(1)}
              </button>
            )
          )}
          <button
            type="button"
            className="viewport-pill"
            disabled={walkStatus.enabled}
            onClick={() => useEditorStore.getState().frameWorld()}
          >
            Frame
          </button>
          <button
            type="button"
            className={
              "viewport-pill viewport-walk-button" +
              (walkStatus.enabled ? " active" : "")
            }
            onClick={toggleWalkMode}
          >
            {walkStatus.enabled ? "Exit walk" : "Walk"}
          </button>
        </div>
      </div>

      {activeTool === "select" && !walkStatus.enabled && (
        <div className="viewport-selection-hint">
          <strong>Selection mode</strong>
          <span>Click two corners or hold Shift and drag</span>
        </div>
      )}

      {walkStatus.enabled && (
        <>
          <div className="walk-crosshair" aria-hidden="true">
            <span className="walk-crosshair-horizontal" />
            <span className="walk-crosshair-vertical" />
          </div>
          <div
            className={
              "walk-mode-guide" +
              (walkStatus.pointerLocked ? " locked" : " unlocked")
            }
          >
            <div className="walk-mode-guide-copy">
              <strong>
                {walkStatus.pointerLocked
                  ? "First-person walk"
                  : "Mouse released"}
              </strong>
              <span>
                WASD move · Mouse look · Space jump · Shift sprint · E interact · Esc unlock
              </span>
            </div>
            {!walkStatus.pointerLocked && (
              <button
                type="button"
                className="walk-resume-button"
                onClick={() =>
                  rendererInstance?.requestWalkPointerLock()
                }
              >
                Resume mouse look
              </button>
            )}
          </div>
        </>
      )}

      <div className="viewport-bottom-overlay">
        <div className="coordinate-readout">
          <span className="coordinate-label">
            {walkStatus.enabled ? "Player" : "Cursor"}
          </span>
          <strong>
            {walkStatus.enabled
              ? `${formatWalkCoordinate(walkStatus.position.x)} / ${formatWalkCoordinate(walkStatus.position.y)} / ${formatWalkCoordinate(walkStatus.position.z)}`
              : cursorPosition
                ? `${cursorPosition.x} / ${cursorPosition.y} / ${cursorPosition.z}`
                : "—"}
          </strong>
        </div>
        <div className="viewport-bottom-controls">
          <div className="visibility-control">
            <span className="coordinate-label">Night vision</span>
            <input
              type="range"
              min="0"
              max="100"
              step="2"
              value={visibilityBoost}
              aria-label="Night vision visibility boost"
              onChange={(event) =>
                updateVisibilityBoost(Number(event.target.value))
              }
            />
            <strong>
              {visibilityBoost === 0 ? "Off" : visibilityBoost + "%"}
            </strong>
            <button
              type="button"
              className="visibility-reset-button"
              disabled={visibilityBoost === 0}
              onClick={() => updateVisibilityBoost(0)}
            >
              Reset
            </button>
          </div>
          <div className="layer-control">
            <span className="coordinate-label">Visible Y</span>
            <input
              type="range"
              min="-8"
              max="64"
              value={visibleMaximumY}
              onChange={(event) =>
                setVisibleMaximumY(Number(event.target.value))
              }
            />
            <strong>{visibleMaximumY}</strong>
          </div>
        </div>
      </div>

      <div
        className={
          "viewport-toast" + (toastMessage ? " visible" : "")
        }
        role="status"
      >
        {toastMessage}
      </div>
    </section>
  );
}
